import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  Slider,
  CircularProgress,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

function ReviewDialog({ open, onClose, request, onSubmit, isSubmitting = false, submittingAction = null }) {
  const aiRawPercent = Math.min(
    100,
    Math.max(0, Number(request?.ai_verified_percent) || 0)
  );
  const userRequestedPercent = Math.min(
    100,
    Math.max(0, Number(request?.manual_user_percentage) || 0)
  );
  const [manualPercentage, setManualPercentage] = useState(
    userRequestedPercent >= 25 ? userRequestedPercent : 25
  );

  useEffect(() => {
    if (!request) return;
    const requested = Math.min(100, Math.max(0, Number(request.manual_user_percentage) || 0));
    setManualPercentage(requested >= 25 ? requested : 25);
  }, [request]);

  const handleSliderChange = (event, newValue) => {
    setManualPercentage(newValue);
  };

  const handleVerify = () => {
    if (isSubmitting) return;
    onSubmit({
      review_request_id: request._id,
      status: "approved",
      approved_milestone_percent: manualPercentage,
    });
  };

  const handleReject = () => {
    if (isSubmitting) return;
    onSubmit({
      review_request_id: request._id,
      status: "rejected",
    });
  };

  const percentageMarks = [
    { value: 25, label: "25%" },
    { value: 50, label: "50%" },
    { value: 75, label: "75%" },
    { value: 100, label: "100%" },
  ];

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <SoftTypography variant="h5" fontWeight="medium">
          Request Review
        </SoftTypography>
      </DialogTitle>
      <DialogContent>
        <SoftBox py={2}>
          <SoftBox mb={3}>
            <SoftTypography variant="h6" fontWeight="medium" mb={2}>
              Visual Comparison
            </SoftTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card>
                  <SoftBox p={2}>
                    <SoftTypography variant="button" fontWeight="medium" mb={1} display="block">
                      Project Image
                    </SoftTypography>
                    <CardMedia
                      component="img"
                      image={request?.original_image}
                      alt="Project Image"
                      sx={{
                        width: "100%",
                        height: 200,
                        objectFit: "cover",
                        borderRadius: 1,
                      }}
                    />
                  </SoftBox>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <SoftBox p={2}>
                    <SoftTypography variant="button" fontWeight="medium" mb={1} display="block">
                      User Photo
                    </SoftTypography>
                    <CardMedia
                      component="img"
                      image={request?.current_image}
                      alt="User Photo"
                      sx={{
                        width: "100%",
                        height: 200,
                        objectFit: "cover",
                        borderRadius: 1,
                      }}
                    />
                  </SoftBox>
                </Card>
              </Grid>
            </Grid>
          </SoftBox>

          <SoftBox mb={3}>
            <SoftTypography variant="h6" fontWeight="medium" mb={2}>
              System Analysis
            </SoftTypography>
            <Card>
              <SoftBox p={3}>
                <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <SoftBox>
                    <SoftTypography variant="button" fontWeight="medium" mb={1}>
                      AI Claimed
                    </SoftTypography>
                    <SoftTypography variant="h4" fontWeight="bold" color="info">
                      {aiRawPercent}%
                    </SoftTypography>
                  </SoftBox>
                  <Box position="relative" display="inline-flex">
                    <CircularProgress
                      variant="determinate"
                      value={aiRawPercent}
                      size={80}
                      thickness={8}
                      color="info"
                    />
                    <Box
                      top={0}
                      left={0}
                      bottom={0}
                      right={0}
                      position="absolute"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="caption" component="div" color="text.secondary">
                        {`${aiRawPercent}%`}
                      </Typography>
                    </Box>
                  </Box>
                </SoftBox>

                <SoftBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  py={2}
                  sx={{ borderTop: "1px solid", borderColor: "grey.200" }}
                >
                  <SoftBox>
                    <SoftTypography variant="button" fontWeight="medium" mb={0.5}>
                      User Requested
                    </SoftTypography>
                    <SoftTypography variant="h5" fontWeight="bold" color="success">
                      {userRequestedPercent}%
                    </SoftTypography>
                  </SoftBox>
                  <Box position="relative" display="inline-flex">
                    <CircularProgress
                      variant="determinate"
                      value={userRequestedPercent}
                      size={64}
                      thickness={8}
                      color="success"
                    />
                    <Box
                      top={0}
                      left={0}
                      bottom={0}
                      right={0}
                      position="absolute"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="caption" component="div" color="text.secondary">
                        {`${userRequestedPercent}%`}
                      </Typography>
                    </Box>
                  </Box>
                </SoftBox>

                <SoftBox pt={2} sx={{ borderTop: "1px solid", borderColor: "grey.200" }}>
                  <SoftTypography variant="button" fontWeight="medium" mb={1} display="block">
                    AI Reasoning
                  </SoftTypography>
                  <SoftTypography variant="body2" color="text" sx={{ lineHeight: 1.6 }}>
                    {request?.ai_reasoning?.trim()
                      ? request.ai_reasoning
                      : "No AI reasoning available for this request."}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </Card>
          </SoftBox>

          <SoftBox mb={3}>
            <SoftTypography variant="h6" fontWeight="medium" mb={2}>
              Approve Milestone
            </SoftTypography>
            <Card>
              <SoftBox p={3}>
                <SoftTypography variant="button" fontWeight="medium" mb={2} display="block">
                  Adjust the milestone to approve for this user
                </SoftTypography>
                <Box sx={{ px: 2 }}>
                  <Slider
                    value={manualPercentage}
                    onChange={handleSliderChange}
                    marks={percentageMarks}
                    step={25}
                    min={25}
                    max={100}
                    disabled={isSubmitting}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}%`}
                    color="info"
                  />
                </Box>
                <SoftBox mt={2} display="flex" justifyContent="center">
                  <SoftTypography variant="h4" fontWeight="bold" color="info">
                    {manualPercentage}%
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </Card>
          </SoftBox>
        </SoftBox>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <SoftButton onClick={onClose} color="secondary" variant="outlined" disabled={isSubmitting}>
          Cancel
        </SoftButton>
        <SoftButton
          onClick={handleReject}
          color="error"
          variant="gradient"
          disabled={isSubmitting}
        >
          {isSubmitting && submittingAction === "rejected" && (
            <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
          )}
          {isSubmitting && submittingAction === "rejected" ? "Rejecting..." : "Reject"}
        </SoftButton>
        <SoftButton
          onClick={handleVerify}
          color="success"
          variant="gradient"
          disabled={isSubmitting}
        >
          {isSubmitting && submittingAction === "approved" && (
            <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
          )}
          {isSubmitting && submittingAction === "approved" ? "Verifying..." : "Verify"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

ReviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  request: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  submittingAction: PropTypes.oneOf(["approved", "rejected", null]),
};

export default ReviewDialog;
